import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import ApiService from '../../services/apiService';
import { convertFirebaseTimestamp, formatDate } from '../../utils/dateUtils';

const Profiles = () => {
  const { currentUser, userProfile, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [learningSessions, setLearningSessions] = useState([]);
  const [teachers, setTeachers] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isAdmin()) {
      navigate('/admin');
      return;
    }

    if (currentUser) {
      fetchProfile();
    }
  }, [currentUser, isAdmin, navigate]);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      
      // Use userProfile from context if available, otherwise fetch from API
      if (userProfile) {
        setProfile(userProfile);
      } else {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/profile/${currentUser.uid}`,
          {
            headers: {
              'Authorization': `Bearer ${await currentUser.getIdToken()}`
            }
          }
        );

        if (!response.ok) {
          throw new Error('Failed to fetch profile');
        }

        const profileData = await response.json();
        setProfile(profileData);
      }

      // Fetch learning sessions and teachers
      await Promise.all([
        fetchLearningSessions(),
        fetchTeachers()
      ]);
    } catch (err) {
      console.error('Error fetching profile:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchLearningSessions = async () => {
    try {
      const token = await currentUser.getIdToken();
      const sessionsData = await ApiService.getUserLearningSessions(currentUser.uid, token);
      console.log("Sessions data: ", sessionsData);
      setLearningSessions(sessionsData.learningSessions || []);
    } catch (error) {
      console.error('Error fetching sessions:', error);
      setLearningSessions([]);
    }
  };

  const fetchTeachers = async () => {
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/teachers`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch teachers');
      }

      const teachersData = await response.json();
      // Convert array to object with teacher ID as key for easy lookup
      const teachersMap = {};
      teachersData.forEach(teacher => {
        teachersMap[teacher.id] = teacher;
      });
      setTeachers(teachersMap);
    } catch (error) {
      console.error('Error fetching teachers:', error);
      setTeachers({});
    }
  };

  // Helper function to convert Firebase timestamp to Date

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="max-w-md mx-auto px-4 pt-20 pb-8">
        {/* Title */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Your Profile</h1>
          <p className="text-gray-600 mt-2">View and manage your profile information</p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-600">{error}</p>
          </div>
        )}

        {/* Profile Card */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="p-6">
            <div className="flex flex-col items-center">
              {/* Profile Image */}
              <div className="h-24 w-24 mb-4">
                {profile?.picture ? (
                  <img
                    className="h-24 w-24 rounded-full object-cover"
                    src={profile.picture}
                    alt={`${profile.firstName} ${profile.lastName}`}
                  />
                ) : (
                  <div className="h-24 w-24 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                    <span className="text-2xl font-bold text-white">
                      {(profile?.firstName || currentUser?.displayName?.split(' ')[0] || 'U').charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
              </div>
              
              {/* Profile Info */}
              <div className="text-center">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {profile ? `${profile.firstName} ${profile.lastName}` : currentUser?.displayName || 'User'}
                </h3>
                <p className="text-gray-600 text-sm mb-3">{currentUser?.email}</p>
                
                {/* Role Badge */}
                <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                  profile?.role === 'ADMIN' || profile?.role === 'admin'
                    ? 'bg-blue-100 text-blue-800'
                    : profile?.role === 'TEACHER' || profile?.role === 'teacher'
                    ? 'bg-green-100 text-green-800'
                    : profile?.role === 'PARENT' || profile?.role === 'parent'
                    ? 'bg-purple-100 text-purple-800'
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  {(profile?.role || 'STUDENT').charAt(0).toUpperCase() + (profile?.role || 'STUDENT').slice(1).toLowerCase()}
                </span>
              </div>
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="bg-gray-50 px-6 py-4">
            <button
              onClick={() => navigate('/profile')}
              className="w-full px-4 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all font-medium"
            >
              More Details
            </button>
          </div>
        </div>

        {/* Learning Sessions List */}
        {learningSessions.length > 0 && (
          <div className="mt-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Learning Sessions</h2>
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Attention</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Teacher</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {learningSessions.slice(0, 5).map((session) => {
                      const teacher = teachers[session.teacherId];
                      const sessionDate = convertFirebaseTimestamp(session.createdAt);
                      
                      return (
                        <tr key={session.id} className="hover:bg-gray-50">
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                            {sessionDate ? (
                              <div>
                                <div className="font-medium">
                                  {sessionDate.toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric'
                                  })}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {sessionDate.toLocaleTimeString('en-US', {
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    hour12: true
                                  })}
                                </div>
                              </div>
                            ) : 'N/A'}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                            {session.time_minutes || 0} min
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                            {session.attention_percentage || 0}%
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                            {teacher ? (
                              <div className="flex items-center">
                                <div className="h-8 w-8 mr-3">
                                  {teacher.picture ? (
                                    <img
                                      className="h-8 w-8 rounded-full object-cover"
                                      src={teacher.picture}
                                      alt={teacher.name}
                                    />
                                  ) : (
                                    <div className="h-8 w-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                                      <span className="text-xs font-bold text-white">
                                        {teacher.name.charAt(0)}
                                      </span>
                                    </div>
                                  )}
                                </div>
                                <div>
                                  <div className="font-medium text-gray-900">{teacher.name}</div>
                                  <div className="text-xs text-gray-500">
                                    {teacher.level} • {teacher.specialty}
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <span className="text-gray-500">No Teacher</span>
                            )}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm">
                            <button
                              onClick={() => navigate(`/chat/${currentUser.uid}?session=${session.id}`)}
                              className="text-blue-600 hover:text-blue-800 font-medium"
                            >
                              View
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              
              {/* Show All Sessions Button */}
              {learningSessions.length > 5 && (
                <div className="bg-gray-50 px-4 py-3 text-center">
                  <button
                    onClick={() => navigate('/dashboard')}
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                  >
                    View All Sessions ({learningSessions.length})
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* No Sessions Message */}
        {learningSessions.length === 0 && !loading && (
          <div className="mt-8">
            <div className="bg-white rounded-lg shadow-md p-6 text-center">
              <svg className="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
              <h3 className="text-lg font-bold text-gray-900 mb-2">No Learning Sessions Yet</h3>
              <p className="text-gray-600 mb-4">Start your first learning session to see your progress here!</p>
              <button
                onClick={() => navigate('/teachers')}
                className="px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all text-sm font-medium"
              >
                Start Learning
              </button>
            </div>
          </div>
        )}

        {/* Back to Account Button */}
        <div className="text-center mt-6">
          <button
            onClick={() => navigate('/account')}
            className="text-blue-600 text-sm font-medium hover:text-blue-800 transition-colors"
          >
            ← Back to Account
          </button>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Profiles;

